import {
  StaffPermissionFilterGroup,
  staffUserConfigurablePermissionFilterGroups,
} from '../../../../constants/staffPermissionFilterGroups';
import { LOCATIONS } from '../../../../constants';
import { hasReentryPlanLfaForLocationIds } from '../../reentry-planning/utils/reentry-planning.util';

/**
 * Multi-section rendering + parent/child toggle model for the staff permission catalog
 * (create-staff step and staff-profile Permissions tab).
 *
 * The Janus catalog is a flat list of rows carrying their own DB copy:
 *   `{ permissionId, permissionName, permissionDisplayName, description,
 *      groupName, groupDisplayName, grantedByRole, effectiveGranted }`.
 *
 * Everything about which sections render, their order, their LFA gate, and any parent/child
 * bundle lives in the single {@link PERMISSION_GROUP_CONFIG} map keyed by `groupName`. Adding a
 * NEW group later only requires adding one entry to that map — no rendering code changes.
 *
 * The toggle UI still reads and writes the underlying `permissionId`-keyed state, so the
 * existing create (sparse diff vs `grantedByRole`) and profile (full-list PUT) save paths
 * keep working unchanged — parent/child toggles simply fan out to the individual rows.
 */

/** Group name for the Reentry Planning catalog slice (matches Janus `permissions.group_name`). */
export const REENTRY_PLANNING_GROUP_NAME = StaffPermissionFilterGroup.REENTRY_PLANNING;

/** Configurable reentry permission slugs shown as product toggles (hidden pair keys stay in Janus). */
const CASE_PLAN_REENTRY_EDIT_DETAIL = 'case_plan_reentry_edit_detail';
const CASE_PLAN_REENTRY_VIEW_ANY_DETAIL = 'case_plan_reentry_view_any_detail';
const CASE_PLAN_REENTRY_CREATE = 'case_plan_reentry_create';
const CASE_PLAN_REENTRY_CREATE_TEMPLATE = 'case_plan_reentry_create_template';

/**
 * Parent toggle: one catalog slug. Label + description come from that row (same as children).
 * Renders ON when the slug is granted; OFF hides and clears the child toggles.
 */
export const REENTRY_PARENT_TOGGLE = Object.freeze({
  key: 'reentry-view-and-edit-plans',
  permissionNames: [CASE_PLAN_REENTRY_EDIT_DETAIL],
});

/**
 * Child toggles: rendered only while the parent is ON. Each child's label + description come
 * from the DB catalog row of its permission name. Only `key` + `permissionNames` are configured here.
 */
export const REENTRY_CHILD_TOGGLES = Object.freeze([
  {
    key: 'reentry-view-all-plans',
    permissionNames: [CASE_PLAN_REENTRY_VIEW_ANY_DETAIL],
  },
  {
    key: 'reentry-create-plans',
    permissionNames: [CASE_PLAN_REENTRY_CREATE],
  },
  {
    key: 'reentry-create-templates',
    permissionNames: [CASE_PLAN_REENTRY_CREATE_TEMPLATE],
  },
]);

/**
 * Single source of truth for section rendering, keyed by `groupName`.
 *
 * Each entry: `{ order, gate, parentChild? }`
 *  - `order`   — section sort position (ascending).
 *  - `gate`    — predicate `(gateContext) => boolean` deciding whether the section renders.
 *                `gateContext` is `{ locationIds }` (the target facility ids). Use `() => true`
 *                for always-on groups.
 *  - `parentChild` (optional) — `{ parent, children }` bundle config. When present, the section
 *                renders as a parent toggle + conditional child toggles instead of flat rows.
 *
 * To add a NEW group later, add ONE entry here — no other rendering changes required, e.g.:
 *
 *   [StaffPermissionFilterGroup.VOICE]: {
 *     order: 2,
 *     gate: ({ locationIds }) => hasVoiceLfaForLocationIds(LOCATIONS, locationIds),
 *     // omit `parentChild` for a plain flat section
 *   },
 */
export const PERMISSION_GROUP_CONFIG = Object.freeze({
  [StaffPermissionFilterGroup.FEEDS]: Object.freeze({
    order: 0,
    gate: () => true,
  }),
  [REENTRY_PLANNING_GROUP_NAME]: Object.freeze({
    order: 1,
    gate: ({ locationIds } = {}) => hasReentryPlanLfaForLocationIds(LOCATIONS, locationIds),
    parentChild: Object.freeze({
      parent: REENTRY_PARENT_TOGGLE,
      children: REENTRY_CHILD_TOGGLES,
    }),
  }),
});

/**
 * Default config for groups present in the catalog but absent from {@link PERMISSION_GROUP_CONFIG}.
 * Chosen behavior: render as a plain flat section (always visible, ordered last). This is the
 * graceful/safer default — the catalog is already server-gated to user-configurable rows that
 * were explicitly requested via the filter-groups allow-list, so anything returned is meant to
 * be shown; hiding it would silently drop grantable permissions.
 */
const DEFAULT_GROUP_CONFIG = Object.freeze({
  order: Number.MAX_SAFE_INTEGER,
  gate: () => true,
});

function groupConfigFor(groupName) {
  return PERMISSION_GROUP_CONFIG[groupName] ?? DEFAULT_GROUP_CONFIG;
}

/**
 * Location ids whose person_location_assn is active.
 *
 * Staff-profile rows carry PLA `active: true|false`. Create-staff selections are new
 * assignments (no PLA yet) and omit `active` — treat missing `active` as active so both
 * surfaces share one rule. Disabled-location LFAs must not leak into permission gates.
 *
 * @param {Array<{ locationId?: number|string, id?: number|string, active?: boolean }>} [locations]
 * @returns {Array<number|string>}
 */
export function activeLocationIdsFrom(locations) {
  return (locations ?? [])
    .filter((location) => location != null && location.active !== false)
    .map((location) => location.locationId ?? location.id)
    .filter((id) => id !== null && id !== undefined && id !== '');
}

/**
 * Janus `filter_groups` for the user-configurable catalog fetch.
 *
 * Walks {@link staffUserConfigurablePermissionFilterGroups} and keeps a group when:
 *  - it has a {@link PERMISSION_GROUP_CONFIG} entry whose `gate({ locationIds })` is true, or
 *  - it is absent from the map (same safer default as unmapped sections rendering flat).
 *
 * Create-staff and staff-profile pass this list instead of the full allow-list so LFA-gated
 * groups (e.g. `reentry_planning`) are not requested when their gate is currently false.
 *
 * @param {{ locationIds?: Array<number|string> }} [gateContext] — target facility ids for gates
 * @returns {string[]}
 */
export function resolveEnabledPermissionFilterGroups({ locationIds } = {}) {
  return staffUserConfigurablePermissionFilterGroups.filter((groupName) => {
    const config = PERMISSION_GROUP_CONFIG[groupName];
    if (!config) {
      return true;
    }
    return Boolean(config.gate({ locationIds }));
  });
}

const normalizeName = (name) => String(name ?? '').toLowerCase();

function findCatalogItem(items, permissionName) {
  const wanted = normalizeName(permissionName);
  return items.find((item) => normalizeName(item?.permissionName) === wanted);
}

/** Toggle descriptor: label/description come from the DB catalog row of the primary permission. */
function buildToggleDescriptor(items, config) {
  const resolvedItems = config.permissionNames
    .map((permissionName) => findCatalogItem(items, permissionName))
    .filter(Boolean);
  if (resolvedItems.length === 0) {
    return null;
  }
  const primary = resolvedItems[0];
  return {
    key: config.key,
    label: primary.permissionDisplayName || primary.permissionName,
    description: primary.description ?? '',
    permissionIds: resolvedItems.map((item) => item.permissionId),
  };
}

/**
 * Builds a parent/child toggle config from a section's catalog items.
 * Returns null when the parent keys are not in the catalog (nothing to render).
 */
export function buildParentChildConfig(items, parentChildConfig) {
  const parent = buildToggleDescriptor(items, parentChildConfig.parent);
  if (!parent) {
    return null;
  }
  const children = parentChildConfig.children
    .map((child) => buildToggleDescriptor(items, child))
    .filter(Boolean);
  return { parent, children };
}

function decorateSection(section, config) {
  const decorated = { ...section, order: config.order };
  if (config.parentChild) {
    decorated.parentChild = buildParentChildConfig(section.items, config.parentChild);
  }
  return decorated;
}

function isSectionVisible(section, gateContext) {
  const config = groupConfigFor(section.groupName);
  if (!config.gate(gateContext)) {
    return false;
  }
  // Bundle groups need a renderable parent/child config (parent keys present in the catalog).
  if (config.parentChild) {
    return Boolean(section.parentChild);
  }
  return section.items.length > 0;
}

function compareSectionsByOrder(a, b) {
  const delta = a.order - b.order;
  if (delta !== 0) {
    return delta;
  }
  return String(a.groupDisplayName).localeCompare(String(b.groupDisplayName));
}

/**
 * Groups the flat Janus catalog into ordered, gated sections driven entirely by
 * {@link PERMISSION_GROUP_CONFIG}. Section headers come from the DB `groupDisplayName`.
 *
 * @param {Array} catalog — Janus permission rows
 * @param {{ locationIds?: Array<number|string> }} [gateContext] — target facility ids for gates
 * @returns {Array<{ groupName, groupDisplayName, items, order, parentChild? }>}
 */
export function buildPermissionSections(catalog, gateContext = {}) {
  if (!Array.isArray(catalog) || catalog.length === 0) {
    return [];
  }

  const order = [];
  const byGroup = new Map();
  catalog.forEach((item) => {
    const groupName = normalizeName(item?.groupName);
    if (!byGroup.has(groupName)) {
      byGroup.set(groupName, {
        groupName,
        groupDisplayName: item?.groupDisplayName || item?.groupName || '',
        items: [],
      });
      order.push(groupName);
    }
    byGroup.get(groupName).items.push(item);
  });

  return order
    .map((groupName) => decorateSection(byGroup.get(groupName), groupConfigFor(groupName)))
    .filter((section) => isSectionVisible(section, gateContext))
    .sort(compareSectionsByOrder);
}

/** True when every bundled permission id is granted in the current toggle state. */
export function areAllGranted(permissionIds, toggleState) {
  return (
    permissionIds.length > 0 && permissionIds.every((id) => Boolean(toggleState?.[id]))
  );
}

/** Expands a bundle of permission ids into `{ permissionId, enabled }` change rows. */
export function buildToggleChanges(permissionIds, enabled) {
  return permissionIds.map((permissionId) => ({ permissionId, enabled }));
}

/**
 * Parent toggle change rows: ON grants the parent keys; OFF clears the parent keys AND every
 * child key (children are hidden while the parent is OFF).
 */
export function buildParentToggleChanges(parentChild, enabled) {
  if (enabled) {
    return buildToggleChanges(parentChild.parent.permissionIds, true);
  }
  const childPermissionIds = parentChild.children.flatMap((child) => child.permissionIds);
  return buildToggleChanges([...parentChild.parent.permissionIds, ...childPermissionIds], false);
}
